/**
 * SA-1093: the pure half of HTML email in the Inbox. The bubble fetches the
 * stored HTML and the inline images; this module sanitizes the HTML with
 * DOMPurify, rewrites `cid:` references to the fetched blobs, and composes
 * the document the sandboxed frame shows. Nothing here touches the page.
 *
 * - `WHOLE_DOCUMENT` keeps the mail's own `<head><style>` blocks, which
 *   Outlook mail depends on; a bare fragment is wrapped in a document.
 * - The `cid:` rewrite runs in `afterSanitizeAttributes`, after DOMPurify's
 *   own URI check: its default pattern does not pass `blob:`, so a rewrite
 *   before the check would be stripped.
 * - The CSP meta is the FIRST head child of the composed document: no load
 *   leaves the frame until "Download External Images" adds `https:`.
 * - The composed document reads the mail's head, its body and the body's own
 *   attributes (handlers never, SA-1105) from ONE inert parse of the
 *   sanitized string, never by cutting the string with a pattern: a
 *   serializer may leave `>` raw inside an attribute value, and a pattern
 *   would end there and spill the rest into the document (final review).
 * - An attribute's URL is read as a browser's URL parser reads it (`urlText`):
 *   a leading control character or a tab inside hides neither a fragment nor
 *   a `cid:` (SA-1122).
 * - `citedCids` is the rewrite's own record: one sanitize pass that notes every
 *   Content-ID the hook looks up, so the frame fetches only what the rewrite
 *   would place (SA-1122). An element the sanitizer removes, a `<source>`
 *   srcset and the srcset of an image whose `src` the rows can show are never
 *   counted.
 * - SA-1309: `foldQuotedHtml` removes a reply's trailing quoted block, found by its client's
 *   own markup (`HEADER_MARKERS` fold to the end, `CONTAINER_MARKERS`, Lark Mail's since
 *   SA-1311, only when trailing). It never folds to nothing and never throws.
 */
import DOMPurify from 'dompurify'
import type { InboxMessage } from '@/types/inbox'

export const SANDBOX = 'allow-same-origin allow-popups allow-popups-to-escape-sandbox'

export type HtmlState = 'html' | 'too_large' | 'store_failed' | 'none'

/** Why a body row has, or has not, a stored HTML version. */
export function htmlStateOf(message: InboxMessage): HtmlState {
  const meta = message.channelMetadata ?? {}
  if (typeof meta.html_ref === 'string' && meta.html_ref) return 'html'
  const missing = meta.html_missing
  if (missing === 'too_large') return 'too_large'
  if (typeof missing === 'string' && missing) return 'store_failed'
  return 'none'
}

/** An attribute's URL as a browser's URL parser reads it: every tab and
 * newline removed, then leading and trailing C0 controls and spaces stripped
 * (SA-1122). `trim()` alone keeps `\x01#_top` a fragment the browser follows. */
export function urlText(value: string): string {
  // eslint-disable-next-line no-control-regex
  return value.replace(/[\t\n\r]/g, '').replace(/^[\u0000-\u0020]+|[\u0000-\u0020]+$/g, '')
}

/** The one reading of a Content-ID: read as a URL (`urlText`), `cid:`
 * dropped, brackets dropped, trimmed, lower-cased. The inline-row index and
 * the rewrite agree on it. */
export function cidKey(value: string): string | null {
  let v = urlText(value).trim()
  if (v.toLowerCase().startsWith('cid:')) v = v.slice(4)
  v = v.trim()
  if (v.startsWith('<') && v.endsWith('>')) v = v.slice(1, -1)
  v = v.trim().toLowerCase()
  return v || null
}

export function cspFor(externalImages: boolean): string {
  const img = externalImages ? 'data: blob: https:' : 'data: blob:'
  return `default-src 'none'; img-src ${img}; style-src 'unsafe-inline'`
}

const URL_ATTRS = ['src', 'background', 'poster'] as const

/** A URL attribute's value that cites a Content-ID. */
function isCidRef(value: string | null): value is string {
  return value !== null && urlText(value).toLowerCase().startsWith('cid:')
}

const SRCSET_SPACE = /[\t\n\f\r ]/

/** The candidate URLs of a `srcset`, split as the HTML parser splits them: a
 * URL runs to the next space (a `data:` URL keeps its commas) and loses any
 * trailing comma; its descriptors run to the next comma outside parentheses. */
function srcsetUrls(srcset: string): string[] {
  const urls: string[] = []
  let i = 0
  while (i < srcset.length) {
    while (i < srcset.length && (SRCSET_SPACE.test(srcset[i]) || srcset[i] === ',')) i++
    const start = i
    while (i < srcset.length && !SRCSET_SPACE.test(srcset[i])) i++
    const url = srcset.slice(start, i)
    if (!url) break
    if (url.endsWith(',')) {
      urls.push(url.replace(/,+$/, ''))
      continue
    }
    urls.push(url)
    let inParens = false
    for (; i < srcset.length; i++) {
      const c = srcset[i]
      if (c === '(') inParens = true
      else if (c === ')') inParens = false
      else if (c === ',' && !inParens) {
        i++
        break
      }
    }
  }
  return urls
}

/** SA-1122: the `srcset` candidates whose URL is a `cid:`, in order. A URL
 * that only contains `cid:` past its start is not one. */
function srcsetCids(srcset: string): string[] {
  return srcsetUrls(srcset).filter((url) => isCidRef(url))
}

/** SA-1110 (R3), SA-1122: the Content-IDs the rewrite looks up, as `cidKey`
 * reads them: one sanitize pass in a record mode in which the keys of
 * `available` (the rows the frame could fetch) map and nothing else does. So
 * an image whose `src` is an available cid never counts its `srcset`, a
 * removed element and a `<source>` srcset count nothing, and a cid in the
 * text or a link is not a citation. The values are the parsed attributes, so
 * an entity-encoded `&#64;` reads as the `@` the rewrite sees. */
export function citedCids(html: string, available: Iterable<string> = []): Set<string> {
  const keys = new Set<string>()
  const placeholders = new Map<string, string>()
  for (const key of available) placeholders.set(key, 'about:blank')
  recording = keys
  try {
    sanitizeEmailHtml(html, { cidUrls: placeholders })
  } finally {
    recording = null
  }
  return keys
}

let purifier: ReturnType<typeof DOMPurify> | null = null
let activeCids: ReadonlyMap<string, string> = new Map()
/** `citedCids`' record: every key the hook looks up, while it runs. */
let recording: Set<string> | null = null

/** The blob URL a cited Content-ID maps to, noting the key while recording. */
function lookUp(value: string): string | undefined {
  const key = cidKey(value)
  if (!key) return undefined
  recording?.add(key)
  return activeCids.get(key)
}

function getPurifier() {
  if (purifier) return purifier
  const p = DOMPurify(window)
  p.addHook('afterSanitizeAttributes', (node) => {
    // SA-1122: DOMPurify (3.4) runs this hook on a node it has just removed
    // too. Nothing detached is ever output, so it is neither rewritten nor
    // counted as cited.
    if (!node.isConnected) return
    // A link and an image-map area alike: a bare href on either would navigate
    // the frame itself, and the sandbox's popups are the one way out.
    if (node.tagName === 'A' || node.tagName === 'AREA') {
      // SA-1105: a fragment-only link (`#_top`) would open the Inbox's own
      // URL in a new tab; it loses its href and stays plain text.
      const href = node.getAttribute('href')
      if (href !== null && urlText(href).startsWith('#')) node.removeAttribute('href')
      node.setAttribute('target', '_blank')
      node.setAttribute('rel', 'noopener noreferrer')
    }
    for (const attr of URL_ATTRS) {
      const value = node.getAttribute(attr)
      if (!isCidRef(value)) continue
      const url = lookUp(value)
      if (url) node.setAttribute(attr, url)
      else node.removeAttribute(attr)
    }
    // SA-1105: a srcset with a cid candidate is removed, so no candidate can
    // drive a load; an image left with no src takes the first cid in it that
    // maps. SA-1122: only a candidate whose URL starts with `cid:` counts.
    const srcset = node.getAttribute('srcset')
    const cids = srcset ? srcsetCids(srcset) : []
    if (cids.length) {
      if (node.tagName === 'IMG' && !node.getAttribute('src')) {
        for (const cid of cids) {
          const url = lookUp(cid)
          if (url) {
            node.setAttribute('src', url)
            break
          }
        }
      }
      node.removeAttribute('srcset')
    }
  })
  purifier = p
  return p
}

export function sanitizeEmailHtml(html: string, opts: { cidUrls: ReadonlyMap<string, string> }): string {
  activeCids = opts.cidUrls
  try {
    return getPurifier().sanitize(html, {
      WHOLE_DOCUMENT: true,
      FORBID_TAGS: ['form', 'input', 'button', 'select', 'textarea', 'svg', 'math'],
      ADD_ATTR: ['target'],
    }) as string
  } finally {
    activeCids = new Map()
  }
}

/** SA-1309: where a mail client starts the earlier email it appends under a reply. A header
 * marker folds everything after it; the last entry is classic desktop Outlook's top-bordered
 * header block, which `isOutlookHeaderBlock` confirms (survey, 19 Oct 2026). Module-private
 * (SA-1311): that selector over-matches without the structure check, so the lists are not a
 * vocabulary for other code to reuse. */
const HEADER_MARKERS: readonly string[] = [
  '#divRplyFwdMsg', '#mail-editor-reference-message-container', 'div[style*="border-top" i]',
]

/** SA-1309: a block that wraps the quoted text; it folds only when nothing follows it. Lark
 * Mail's `lark-mail-quote-<n>` block (SA-1311) trailed its reply in every stored case and opens
 * with the quoted "From:" (survey, 19 Oct 2026); its inner blocks take a hashed id. */
const CONTAINER_MARKERS: readonly string[] = [
  '.gmail_quote_container', '.gmail_quote', 'blockquote[type="cite" i]', 'div[id^="lark-mail-quote-"]',
]

const CLASSIC_OUTLOOK = 'div[style*="border-top" i]'
const ALL_MARKERS = [...HEADER_MARKERS, ...CONTAINER_MARKERS].join(',')
const PLAIN_HEADERS = HEADER_MARKERS.filter((m) => m !== CLASSIC_OUTLOOK).join(',')
const PLAIN_MARKERS = [PLAIN_HEADERS, ...CONTAINER_MARKERS].join(',')

/** Classic Outlook's header: a solid top border, opened by a bold label ending in a colon
 * ("From:", "Von:", "Kimden:"), so the rule never names a language. */
function isOutlookHeaderBlock(el: Element): boolean {
  const style = (el.getAttribute('style') ?? '').replace(/\s+/g, '').toLowerCase()
  if (!style.includes('border-top:solid')) return false
  const bold = el.querySelector('b')
  const label = bold?.textContent?.replace(/\s+/g, ' ').trim() ?? ''
  if (!bold || !label.endsWith(':')) return false
  // The block's text must open with that label, as a header does ("From: …"); a bordered
  // enquiry box with a bold "Route:" further in is not one.
  const first = firstText(el)
  return first !== null && bold.contains(first)
}

/** SA-1311: text with nothing to read: whitespace (`\s` covers the no-break space and U+FEFF)
 * and the zero-width characters U+200B, U+200C, U+200D and U+2060, which mail clients scatter. */
function isBlankText(text: string | null): boolean {
  return (text ?? '').replace(/[\s\u200B-\u200D\u2060]/g, '') === ''
}

/** The first text node with something other than blank text in it, in document order. */
function firstText(node: Node): Node | null {
  if (node.nodeType === Node.TEXT_NODE) return isBlankText(node.textContent) ? null : node
  for (const child of Array.from(node.childNodes)) {
    const hit = firstText(child)
    if (hit) return hit
  }
  return null
}

const isClassicOutlook = (el: Element) => el.matches(CLASSIC_OUTLOOK) && isOutlookHeaderBlock(el)
const isMarker = (el: Element) => el.matches(PLAIN_MARKERS) || isClassicOutlook(el)
const isHeader = (el: Element) => el.matches(PLAIN_HEADERS) || isClassicOutlook(el)

/** Text that is not blank (see `isBlankText`), or an image. */
function hasContent(node: Node): boolean {
  if (node.nodeType === Node.TEXT_NODE) return !isBlankText(node.textContent)
  if (node.nodeType !== Node.ELEMENT_NODE) return false
  const el = node as Element
  if (['STYLE', 'SCRIPT', 'TITLE'].includes(el.tagName)) return false
  if (el.tagName === 'IMG') return true
  return Array.from(el.childNodes).some(hasContent)
}

/** Nothing with content after the node, at its own level or any ancestor's, up to the body. */
function isTrailing(el: Element, body: HTMLElement): boolean {
  for (let cur: Node | null = el; cur && cur !== body; cur = cur.parentNode) {
    for (let sib = cur.nextSibling; sib; sib = sib.nextSibling) {
      if (hasContent(sib)) return false
    }
  }
  return true
}

const isBlank = (n: Node) =>
  n.nodeType === Node.COMMENT_NODE || (n.nodeType === Node.TEXT_NODE && isBlankText(n.textContent))

/** The nearest previous sibling element, past whitespace and comments; null past anything else. */
function previousElement(node: Node): Element | null {
  let prev = node.previousSibling
  while (prev && isBlank(prev)) prev = prev.previousSibling
  return prev?.nodeType === Node.ELEMENT_NODE ? (prev as Element) : null
}

/** Move the start back over what a client writes directly above its marker: Outlook's `<hr>`
 * and `#appendonsend` above a header, Thunderbird's attribution line above a citation. */
function pullBack(start: Element, header: boolean): Element {
  let result = start
  let prev = previousElement(start)
  if (header) {
    while (prev && (prev.tagName === 'HR' || prev.id === 'appendonsend')) {
      result = prev
      prev = previousElement(prev)
    }
  } else if (prev?.matches('div.moz-cite-prefix')) {
    result = prev
  }
  return result
}

/** SA-1309: the element the quoted block starts at: the first marker in document order not
 * inside an earlier one, a header always and a container only when trailing, moved back over
 * its client's lead-in. Null when none qualifies. */
export function findQuotedStart(doc: Document): Element | null {
  const body = doc.body
  if (!body) return null
  // Walk the elements in document order: a selector-list query may hand back an id match first
  // (jsdom's does), which would skip an earlier marker. `getElementsByTagName` is tree order.
  for (const el of Array.from(body.getElementsByTagName('*'))) {
    if (!el.matches(ALL_MARKERS) || !isMarker(el)) continue
    let nested = false
    for (let a = el.parentElement; a && a !== body; a = a.parentElement) {
      if (isMarker(a)) { nested = true; break }
    }
    if (nested) continue
    const header = isHeader(el)
    if (header || isTrailing(el, body)) return pullBack(el, header)
  }
  return null
}

/** SA-1309: the sanitized document with its trailing quoted block removed, or null when there
 * is nothing to fold: no marker, or a fold that would leave the body with no content. Never
 * throws: a failure is no fold, so the email shows whole. */
export function foldQuotedHtml(sanitized: string): string | null {
  try {
    const doc = new DOMParser().parseFromString(sanitized, 'text/html')
    const start = findQuotedStart(doc)
    const last = doc.body?.lastChild
    if (!start || !last) return null
    const range = doc.createRange()
    range.setStartBefore(start)
    range.setEndAfter(last)
    range.deleteContents()
    if (!hasContent(doc.body)) return null
    return doc.documentElement.outerHTML
  } catch (err) {
    console.warn('SA-1309: the quoted fold failed', err)
    return null
  }
}

const RESET = `html, body { background: #fff; color: #111; margin: 0; }
body { padding: 12px; font: 14px/1.45 -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; }
img { max-width: 100%; height: auto; }
table { max-width: 100%; }`

function escapeAttribute(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
}

/** The srcdoc: a doctype, our head (the CSP meta first, the referrer meta,
 * then the reset), the mail's own head children, then its body with the
 * body's own attributes (an Outlook `bgcolor`, a newsletter's `style`;
 * a handler never, whatever it was handed, SA-1105). All three are read from
 * one inert parse of the sanitized string (a `DOMParser` document loads
 * nothing and runs nothing): the head and body content are that parse's own
 * serialization, which the frame's parser reads back element for element (a
 * raw `>` in a nested attribute is still inside its quotes), and the body's
 * attributes go through `escapeAttribute`. */
export function composeEmailDocument(sanitized: string, opts: { externalImages: boolean }): string {
  const parsed = new DOMParser().parseFromString(sanitized, 'text/html')
  const bodyAttrs = Array.from(parsed.body.attributes)
    .filter((a) => !/^on/i.test(a.name))
    .map((a) => ` ${a.name}="${escapeAttribute(a.value)}"`)
    .join('')
  return `<!doctype html><html><head><meta http-equiv="Content-Security-Policy" content="${cspFor(opts.externalImages)}">`
    + '<meta name="referrer" content="no-referrer">'
    + `<meta charset="utf-8"><style>${RESET}</style>${parsed.head.innerHTML}</head><body${bodyAttrs}>${parsed.body.innerHTML}</body></html>`
}
